import { LUXURY_COMPLIMENTARY_NIGHT_PROMOTION_ENTRYPOINTS } from "@capone/experiments";
import {
  DefaultImageBanner,
  GENERIC_HOTEL_PROMOTION_OFFER_IMAGE,
  TRAVEL_CREDIT_TOOLTIP_TEXT,
  TRAVEL_OFFERS_TOOLTIP_TEXT,
  TRAVEL_WALLET_DRAWER_TITLES,
  TRAVEL_WALLET_MORE_INFO_TEXT,
  TravelOfferCardMinimal,
  TravelWalletDetailsModal,
  TravelWalletDrawer,
  getRestrictedUserTCTexts,
  getTravelOfferCardContent,
  isOfferHSPTiered,
  useDeviceTypes,
  filterOutSpendAndGetOffers,
} from "halifax";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router";
import {
  CorpSessionInfo,
  ENGAGED_OFFER_CTA,
  Funnel,
  TravelWalletOffer,
  VIEW_CREDIT_HISTORY,
  VIEW_TRAVEL_WALLET,
  VIEWED_COMP_NIGHT_BANNER,
  VIEWED_PREMIUM_OFFER_PAGE,
  ViewedCompNightBannerEntryPoint,
  ViewedCompNightBannerProperties,
  ViewedPremiumOfferPageProperties,
  MediaEventType,
  MediaPlacement,
} from "redmond";
import {
  appendIsTravelAppParam,
  COMPLIMENTARY_NIGHT_PROMOTION_BUTTON_TEXT,
  COMPLIMENTARY_NIGHT_PROMOTION_DESCRIPTION,
  COMPLIMENTARY_NIGHT_PROMOTION_TITLE,
  getConfigTenant,
  insertIf,
  isCorpTenant,
  PATH_PREMIUM_OFFERS_SHOP,
  useIsTravelApp,
  getTrackingEntryPoint,
} from "@capone/common";
import { Box } from "@material-ui/core";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { trackMediaEvent } from "../../../../../../../cap1-application/b2b-base/src/api/v1/trackHTSMedia";
import { useViewportObserver } from "../../../../../../../cap1-application/b2b-base/src/hooks/useViewportObserver";
import { ClientContext } from "../../../../App";
import {
  ANNUAL_TRAVEL_CREDITS,
  AVAILABLE,
  CONTROL,
  CREDIT_OFFER_STACKING_V1,
  PREMIER_COLLECTION_EXPERIMENT,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  TRAVEL_SALE,
  TRAVEL_SALE_LEAD_UP,
  TRAVEL_SALE_VARIANTS,
  XMOD_V1_EXPERIMENT,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  useExperiments,
} from "../../../../context/experiments";
import { PATH_HOME, PATH_TRAVEL_SALE } from "../../../../utils/urlPaths";
import { TravelCreditBalanceCard } from "../TravelCreditBalanceCard";
import * as constants from "./constants";
import {
  TRAVEL_SALES_EVENT_ACTIVE_CTA,
  TRAVEL_SALES_EVENT_LEADUP_SUBTITLE,
} from "./constants";
import { TravelWalletDrawerConnectorProps } from "./container";
import "./styles.scss";
import clsx from "clsx";

export interface IFlightTravelWalletDrawerProps
  extends TravelWalletDrawerConnectorProps,
    RouteComponentProps {
  className?: string;
  drawerOpen?: boolean;
  setDrawerOpen?: (open: boolean) => void;
}

export const FlightTravelWalletDrawer = ({
  activeOffers,
  allOffers,
  className,
  rewardsAccounts,
  travelCredit,
  creditBreakdown,
  history,
  drawerOpen,
  setDrawerOpen,
}: IFlightTravelWalletDrawerProps) => {
  const [selectedOffer, setSelectedOffer] = useState<
    { offer: TravelWalletOffer; index?: number } | undefined
  >(undefined);

  const { sessionInfo } = useContext(ClientContext);

  const firstName = sessionInfo?.userInfo?.firstName || "";

  const isNonFinancialUser =
    (sessionInfo as CorpSessionInfo)?.corporateInfo?.cap1Role === "NFU";

  const { matchesMobile } = useDeviceTypes();
  const { isTravelApp } = useIsTravelApp();

  const expState = useExperiments();

  const travelSalesEventVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS,
  );

  const travelOffers = useMemo(
    () =>
      filterOutSpendAndGetOffers(
        travelSalesEventVariant === TRAVEL_SALE_LEAD_UP
          ? activeOffers
          : allOffers,
      ),
    [travelSalesEventVariant, activeOffers, allOffers],
  );

  const totalWalletCount = useMemo(
    () => (travelCredit ? travelOffers.length + 1 : travelOffers.length),
    [travelOffers, travelCredit],
  );

  const creditAndOfferStackingExperimentV1 = getExperimentVariant(
    expState.experiments,
    CREDIT_OFFER_STACKING_V1,
  );
  const isCreditAndOfferStackingExperimentV1 = useMemo(
    () => creditAndOfferStackingExperimentV1 === AVAILABLE,
    [creditAndOfferStackingExperimentV1],
  );

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  );
  const isTravelCreditHistoryExperiment = useMemo(
    () => travelCreditHistoryExperiment === AVAILABLE,
    [travelCreditHistoryExperiment],
  );

  const isAnnualTravelCreditsExperiment =
    getExperimentVariant(expState.experiments, ANNUAL_TRAVEL_CREDITS) ===
    AVAILABLE;

  const showComplimentaryNightPromotionEntrypoints =
    getExperimentVariant(
      expState.experiments,
      LUXURY_COMPLIMENTARY_NIGHT_PROMOTION_ENTRYPOINTS,
    ) === AVAILABLE;

  const showsPremierCollection =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT,
    ) === AVAILABLE;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const getOfferCTAs = useCallback(
    (
      offer: TravelWalletOffer,
      includeViewOfferDetailsCTA?: boolean,
      offerRowIndex?: number,
    ) => {
      const offerIndexToUse =
        offerRowIndex !== undefined ? offerRowIndex + 1 : undefined;
      const trackOfferEngagementEvent = () => {
        trackEvent({
          eventName: ENGAGED_OFFER_CTA,
          properties: {
            location: "flight_wallet_drawer",
            entry_type:
              matchesMobile && !includeViewOfferDetailsCTA ? "modal" : "card",
            index_row_id: offerIndexToUse,
            funnel: offer.funnels.join(","),
          },
          encryptedProperties: [
            offer.trackingPropertiesV2?.encryptedProperties ?? "",
          ],
        });

        trackMediaEvent({
          eventType: MediaEventType.Click,
          trackingProperties: offer.trackingPropertiesV2,
          placement: MediaPlacement.FlightTravelWalletDrawer,
        });
      };

      const { funnels } = offer;
      // Sort funnels to move Home/Homes to the end (has longest text so it should take full width if needed)
      const sortedFunnels = [...funnels].sort((a, b) => {
        const isAHome = a === Funnel.Home || a === Funnel.Homes;
        const isBHome = b === Funnel.Home || b === Funnel.Homes;
        if (isAHome && !isBHome) return 1;
        if (!isAHome && isBHome) return -1;
        return 0;
      });

      const hasHomeFunnel = funnels.some(
        (f) => f === Funnel.Home || f === Funnel.Homes,
      );
      const shouldMakeSecondToLastFullWidth =
        sortedFunnels.length % 2 === 0 &&
        hasHomeFunnel &&
        sortedFunnels.length >= 2;

      const funnelButton = sortedFunnels.map((funnel, index) => {
        const isSecondToLast = index === sortedFunnels.length - 2;
        const isHome = funnel === Funnel.Home || funnel === Funnel.Homes;
        const shouldBeFullWidth =
          isHome || (shouldMakeSecondToLastFullWidth && isSecondToLast);

        switch (funnel) {
          case Funnel.Air:
            return {
              text: "Search flights",
              onClick: () => {
                history.push(PATH_HOME);
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
          case Funnel.Lodging:
            if (
              isCorpTenant(getConfigTenant()) &&
              offer.descriptions &&
              offer.descriptions[0].toLowerCase().includes("hotels + flights")
            ) {
              return {
                text: "Search hotels + flights",
                onClick: () => {
                  history.push("/hotels-flights/");
                  trackOfferEngagementEvent();
                },
                fullWidth: shouldBeFullWidth,
              };
            }
            if (
              offer.descriptions &&
              offer.descriptions[0].includes("Premier Collection")
            ) {
              return {
                text: "Search premium stays",
                onClick: () => {
                  history.push("/premium-stays/");
                  trackOfferEngagementEvent();
                },
                fullWidth: shouldBeFullWidth,
              };
            }
            return {
              text: "Search hotels",
              onClick: () => {
                history.push("/hotels/");
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
          case Funnel.Homes:
          case Funnel.Home:
            return {
              text: "Search vacation rentals",
              onClick: () => {
                history.push("/premium-stays/vacation-rentals/");
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
          case Funnel.Ground:
            return {
              text: "Search cars",
              onClick: () => {
                history.push("/cars/");
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
          case Funnel.Experiences:
          case Funnel.Activity:
            return {
              text: "Search activities",
              onClick: () => {
                history.push("/activities/");
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
          case Funnel.Package:
            return {
              text: "Search packages",
              onClick: () => {
                history.push("/packages/");
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
        }
      });

      return [
        ...funnelButton,
        ...(includeViewOfferDetailsCTA
          ? [
              {
                text: "View offer details",
                onClick: () => {
                  setSelectedOffer({ offer, index: offerRowIndex });
                  trackOfferEngagementEvent();
                },
              },
            ]
          : []),
      ];
    },
    [history],
  );
  const buildComplimentaryNightPromotionCard = (onClick: () => void) => (
    <Box className="comp-night-banner-container">
      <DefaultImageBanner
        imageUrl={GENERIC_HOTEL_PROMOTION_OFFER_IMAGE}
        title={COMPLIMENTARY_NIGHT_PROMOTION_TITLE}
        description={COMPLIMENTARY_NIGHT_PROMOTION_DESCRIPTION(
          showsPremierCollection,
        )}
        buttonText={COMPLIMENTARY_NIGHT_PROMOTION_BUTTON_TEXT}
        buttonAction={onClick}
      />
    </Box>
  );

  const TravelOfferCardWithTracking = ({
    offer,
    ctas,
  }: {
    offer: TravelWalletOffer;
    ctas: ReturnType<typeof getOfferCTAs>;
  }) => {
    const offerContent = getTravelOfferCardContent(
      offer,
      isCreditAndOfferStackingExperimentV1,
    );

    const offerLocation =
      offer.descriptions.length >= 3
        ? offer?.descriptions[2]
        : offer.descriptions[0];

    const viewportObserver = useViewportObserver<HTMLDivElement>(() => {
      trackMediaEvent({
        eventType: MediaEventType.Impression,
        trackingProperties: offer?.trackingPropertiesV2,
        placement: MediaPlacement.FlightTravelWalletDrawer,
      });
    });

    return (
      <div ref={viewportObserver.ref as React.RefObject<HTMLDivElement>}>
        <TravelOfferCardMinimal
          title={
            isOfferHSPTiered(offer.descriptions)
              ? `<strong>${offer.shopPageBanner}</strong>` || offer.title
              : offer.title
          }
          expirationString={offerContent.expirationDetail}
          validityString={offerContent.applicabilityDetail}
          ctas={ctas}
          offerFunnels={offer.funnels}
          offerLocation={offerLocation}
          imageId={offer.imageId}
          imageFolderUrl={offer.imageFolderUrl}
        />
      </div>
    );
  };

  const renderTravelOffersCards = () => {
    const cards = travelOffers.map((offer) => (
      <TravelOfferCardWithTracking
        key={offer.id}
        offer={offer}
        ctas={getOfferCTAs(offer, true)}
      />
    ));
    insertIf(
      cards,
      showComplimentaryNightPromotionEntrypoints,
      buildComplimentaryNightPromotionCard(() => {
        history.push(
          appendIsTravelAppParam(PATH_PREMIUM_OFFERS_SHOP, isTravelApp),
        );
        trackEvent({
          eventName: VIEWED_PREMIUM_OFFER_PAGE,
          properties: {
            entry_point: ViewedCompNightBannerEntryPoint.WALLET_DRAWER_AIR,
          } as ViewedPremiumOfferPageProperties,
        });
      }),
      1,
    );

    insertIf(
      cards,
      cards.length === 0,
      <TravelOfferCardMinimal
        title={constants.NO_OFFERS_AVAIL_TITLE}
        body={constants.NO_OFFER_AVAIL_BODY}
      />,
      0,
    );
    return <> {cards}</>;
  };

  return (
    <>
      {selectedOffer && (
        <TravelWalletDetailsModal
          openModal={!!selectedOffer}
          setOpenModal={() => setSelectedOffer(undefined)}
          offerCardProps={{
            offerContent: getTravelOfferCardContent(
              selectedOffer.offer,
              isCreditAndOfferStackingExperimentV1,
            ),
            ctas: matchesMobile
              ? getOfferCTAs(selectedOffer.offer, false, selectedOffer.index)
              : undefined,
            buttonClassName: "b2b",
            titleLabelText: isOfferHSPTiered(selectedOffer.offer.descriptions)
              ? constants.HSP_OFFER_TITLE_LABEL
              : undefined,
            className: clsx({ xmod: isXmodRedesign }),
          }}
          termsType={matchesMobile ? "accordion" : "link"}
          isMobile={matchesMobile}
          offerFunnels={selectedOffer.offer.funnels}
          offerLocation={selectedOffer.offer.descriptions[2]}
          isTravelSale={travelSalesEventVariant !== CONTROL}
          imageId={selectedOffer.offer.imageId}
          imageFolderUrl={selectedOffer.offer.imageFolderUrl}
          onViewCreditHistory={() =>
            trackEvent({
              eventName: VIEW_CREDIT_HISTORY,
              properties: {},
            })
          }
        />
      )}
      <TravelWalletDrawer
        creditBalanceCard={
          <TravelCreditBalanceCard
            isTravelCreditHistoryExperiment={isTravelCreditHistoryExperiment}
            isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
            isMobile={matchesMobile}
            onViewCreditHistory={() =>
              trackEvent({
                eventName: VIEW_CREDIT_HISTORY,
                properties: {},
              })
            }
          />
        }
        offersCards={renderTravelOffersCards()}
        travelCreditTooltipText={TRAVEL_CREDIT_TOOLTIP_TEXT(
          isCreditAndOfferStackingExperimentV1,
          isNonFinancialUser,
        )}
        travelOfferTooltipText={TRAVEL_OFFERS_TOOLTIP_TEXT(
          isCreditAndOfferStackingExperimentV1,
        )}
        moreInfoText={TRAVEL_WALLET_MORE_INFO_TEXT}
        drawerTitles={TRAVEL_WALLET_DRAWER_TITLES(travelOffers)}
        className={className}
        isMobile={matchesMobile}
        offersCount={totalWalletCount}
        rewardsAccounts={rewardsAccounts}
        firstName={firstName}
        restrictedUserTCTexts={getRestrictedUserTCTexts(
          rewardsAccounts,
          creditBreakdown,
        )}
        creditBreakdown={creditBreakdown}
        travelSalesEventBannerPlacement={
          travelSalesEventVariant !== CONTROL
            ? travelSalesEventVariant
            : undefined
        }
        travelSalesBannerProps={
          travelSalesEventVariant !== CONTROL
            ? {
                subtitle:
                  travelSalesEventVariant === TRAVEL_SALE_LEAD_UP
                    ? TRAVEL_SALES_EVENT_LEADUP_SUBTITLE
                    : constants.TRAVEL_SALES_EVENT_ACTIVE_SUBTITLE,
                ctaText:
                  travelSalesEventVariant === TRAVEL_SALE_LEAD_UP
                    ? constants.TRAVEL_SALES_EVENT_LEADUP_CTA
                    : TRAVEL_SALES_EVENT_ACTIVE_CTA,
                ctaOnClick: () => {
                  const path = `${PATH_TRAVEL_SALE}?entryType=travel_wallet_drawer_air`;
                  matchesMobile
                    ? history.push(path)
                    : window.open(path, "_blank");
                },
              }
            : undefined
        }
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        showFloatingEntryPoint={!setDrawerOpen}
        includeOffers
        onOpenDrawer={() => {
          trackEvent({
            eventName: VIEW_TRAVEL_WALLET,
            properties: {
              entry_point: getTrackingEntryPoint(window.location.pathname),
            },
          });
          if (showComplimentaryNightPromotionEntrypoints) {
            trackEvent({
              eventName: VIEWED_COMP_NIGHT_BANNER,
              properties: {
                step: ViewedCompNightBannerEntryPoint.WALLET_DRAWER_AIR,
              } as ViewedCompNightBannerProperties,
            });
          }
        }}
      />
    </>
  );
};
